import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getTokenExpiryDelayMs,
  handleExpiredSession,
  installSessionExpiryInterceptor,
  MAX_TIMEOUT_MS,
  resetSessionExpiryGuard,
  shouldExpireTokenNow,
} from "../auth/sessionExpiry";

const useTokenExpiry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user?.user);
  const jwtUpdatedAt = useSelector((state) => state.user?.jwtUpdatedAt);

  const expireSession = useCallback(() => {
    handleExpiredSession({ dispatch, navigate });
  }, [dispatch, navigate]);

  const expireSessionRef = useRef(expireSession);
  expireSessionRef.current = expireSession;

  useEffect(() => {
    return installSessionExpiryInterceptor(() => expireSessionRef.current());
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");

    if (token && !shouldExpireTokenNow(token)) {
      resetSessionExpiryGuard();
    }

    if (!token) {
      return undefined;
    }

    if (shouldExpireTokenNow(token)) {
      expireSessionRef.current();
      return undefined;
    }

    const delay = getTokenExpiryDelayMs(token);
    if (delay == null) {
      return undefined;
    }

    let timeoutId = setTimeout(
      function tick() {
        const current = localStorage.getItem("jwtToken");
        if (!current || shouldExpireTokenNow(current)) {
          expireSessionRef.current();
          return;
        }

        const nextDelay = getTokenExpiryDelayMs(current);
        if (nextDelay == null) {
          return;
        }

        timeoutId = setTimeout(tick, Math.min(nextDelay, MAX_TIMEOUT_MS));
      },
      Math.min(delay, MAX_TIMEOUT_MS),
    );

    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const current = localStorage.getItem("jwtToken");
      if (current && shouldExpireTokenNow(current)) {
        expireSessionRef.current();
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, jwtUpdatedAt]);
};

export default useTokenExpiry;
